<template>
    <ul
        class="p-thread"
        :class="userType === 'doctor' ? 'p-thread--doctors' : 'p-thread--users'"
        v-chat-scroll>
        <li
            v-for="message in splitMessages"
            class="p-chat"
            :class="messageUserType(message) | morphUserType( 'p-chat--' )">
            <div class="p-chat__speaker d-flex align-items-center">
                <div class="p-chat__speaker__image">
                    <img
                        v-if="hasProfileImage(message)"
                        :src="message.user.presigned_url">
                    <img
                        v-else
                        src="/images/common/profile_default.jpg">
                </div>

                <div class="p-chat__speaker__info u-palt">
                    <div class="text-muted">
                        <span>{{ helper.dateTimeForHuman(message.created_at, timezone) }}</span>
                        <span
                            class="font-weight-bold"
                            :class="helper.dayForHumanCSS(message.created_at , timezone)">
                            {{ helper.dayForHuman(message.created_at, timezone, { bracket: true }) }}
                        </span>
                        <span class="ml-1">
                            <timeago :datetime="Date.parse(helper.userTime(message.created_at, timezone).format('YYYY-MM-DD HH:mm:ss.SSSZ'))"></timeago>
                        </span>
                    </div>
                    <div
                        v-if="message.user !== null"
                        class="font-weight-bold">
                        {{ message.user.full_name }}
                    </div>
                    <div
                        v-else
                        class="text-muted">
                        {{ `退会済の${messageUserTypeName(message)}` }}
                    </div>
                </div>
            </div>

            <div class="p-chat__body">
                <div class="p-chat__body__content">
                    <div v-for="block in message.blocks">
                        <div v-for="b in block" class="d-inline">
                            <chat-block :block="b"/>
                        </div>
                    </div>
                </div>
                <div v-for="image in images" class="mt-2">
                    <div v-if="message.id === image.message_id">
                        <template v-if="image.deleted_at === null">
                            <div v-if="image.url.slice(-4) === '.pdf'">
                                <b-img
                                    src="/images/common/icon_pdf.svg"
                                    class="mb-1 ml-3 mr-3 consultation-message-pdf">
                                </b-img>
                            </div>
                            <div v-else>
                                <img
                                    :src="image.presigned_url"
                                    width="120px"
                                    style="cursor:pointer;"
                                    @click="openModalImage(image.presigned_url)">
                            </div>
                            <div class="d-flex align-items-center mt-2">
                                <b-button
                                    size="sm"
                                    variant="yokumiru-gray"
                                    :href="image.presigned_url"
                                    target="_blank"
                                    @click="validateUrlExpired(image.presigned_url, $event)">
                                    ダウンロード
                                </b-button>
                                <b-button
                                    v-if="isOwner(message) && !isClosed"
                                    size="sm"
                                    variant="danger"
                                    class="ml-2"
                                    target="_blank"
                                    @click="openModalDeleteFile(image, 'ConsultationMessageImage')">
                                    削除
                                </b-button>
                            </div>
                        </template>
                        <template v-else>
                            <div class="deletedImageBox">
                                <b-icon-trash></b-icon-trash>
                                <span class="text-muted">このファイルは削除されました</span>
                            </div>
                        </template>
                    </div>
                </div>
            </div>
        </li>
    </ul>
</template>

<script>
    import Vue from 'vue'
    import VueChatScroll from 'vue-chat-scroll'
    import VueTimeago from 'vue-timeago'
    import { ja } from 'date-fns/locale'
    import moment from 'moment-timezone'
    import converter from '../../date-converter'
    import YokumiruHelper from '../../YokumiruHelper'
    import constants from '../../constants'

    Vue.use(VueChatScroll)
    Vue.use(VueTimeago, {
        locale: 'ja',
        locales: { ja: ja },
        converter: converter
    })

    export default {
        props: {
            consultation: {
                type: Object,
                required: true,
            },
            messages: {
                type: Array,
                required: true,
            },
            participants: {
                type: Array,
                required: true,
            },
            images: {
                type: Array,
                required: true,
            },
            timezone: {
                type: String,
                required: true,
            },
            userType: {
                type: String,
                required: true,
            },
            isAdmin: {
                type: Boolean,
                default: false,
            },
        },

        data: function () {
            return {
                helper: YokumiruHelper,
                componentCreatedAt: moment().format('YYYY-MM-DD HH:mm:ss'),
            }
        },

        computed: {
            /**
             * 相談終了／キャンセル済み判定
             *
             * @return {boolean}
             */
            isClosed: function () {
                return constants.consultations.closedAll.includes(this.consultation.status)
            },

            /**
             * チャット添付ファイルの所有者ID
             * @return {number|null}
             */
            ownerId: function () {
                if (this.userType === 'user') {
                    return this.consultation.user_id
                }
                else if (this.userType === 'doctor') {
                    return this.consultation.doctor_id
                }
                return null
            },

            /**
             * 1. 改行でsplitして行を生成
             * 2. 行をさらに半角／全角スペースでsplitしてブロックを生成（ブロック単位でURLスキーム判定できるようにする）
             *
             * @return {array}
             */
            splitMessages: function () {
                return this.messages.map((message) => {
                    if (!message.body) {
                        return message
                    }

                    const lines = message.body.split('\n')
                    const blocks = lines.map((line) => line.split(/([ 　])/u))

                    message.blocks = []
                    let idx = 1

                    // 区切り文字を分割直前の要素に含める
                    blocks.forEach((block) => {
                        let msg = block
                        if (block !== ' ' && block !== '　') {
                            if (blocks.length > idx && blocks[idx + 1] === ' ' || blocks[idx + 1] === '　') {
                                msg = msg + blocks[idx + 1]
                            }
                            message.blocks.push(msg)
                        }
                        idx++
                    })

                    return message
                })
            },
        },

        methods: {
            /**
             * プロフィール画像が設定されているユーザのメッセージかどうか
             * @param {object} message
             * @return {boolean}
             */
            hasProfileImage: function (message) {
                return message.user !== null
                    && message.user.presigned_url !== null
                    && message.user.presigned_url !== void(0)
            },

            /**
             * チャット添付ファイルの所有者かどうか
             * @param {object} message
             * @returns {boolean}
             */
            isOwner: function (message) {
                // 管理者モードではすべて所有者とみなす
                if (this.isAdmin) {
                    return true
                }

                if (message.user_id !== this.ownerId) {
                    return false
                }

                if (this.userType === 'user') {
                    return message.user_type.includes('User')
                } else if (this.userType === 'doctor') {
                    return message.user_type.includes('Doctor')
                }
            },

            openModalImage: function (url) {
                bus.$emit('open-modal-image', url)
            },

            openModalDeleteFile: function (image, model) {
                bus.$emit('open-modal-delete-file', image, model)
            },

            /**
             * DLリンクの有効期限バリデーション
             *
             * @param {string} url
             * @param {object} event
             */
            validateUrlExpired: function (url, event) {
                const expiredSec = Number(url.replace(/^.+X-Amz-Expires=([0-9]+).+$/u, '$1'))
                const expiredAt = moment(this.componentCreatedAt).add(expiredSec, 'seconds')
                const expired = moment().isSameOrAfter(expiredAt)

                if (expired) {
                    bus.$emit('alert-warning', 'ダウンロードの有効期限が切れています。ブラウザを再読み込みしてください。')
                    event.preventDefault()
                }
            },

            /**
             * 患者タイプに応じてCSSクラス名を生成
             *
             * @param {object} message
             * @returns {string|null}
             */
            messageUserType: function (message) {
                const participant = this.participants.find((p) => {
                    return p.user_id === message.user_id && p.user_type === message.user_type
                })

                if (participant) {
                    return participant.user_type
                }
                return null
            },

            /**
             * ユーザタイプに応じてCSSクラス名を生成
             *
             * @param {object} message
             * @returns {string}
             */
            messageUserTypeName: function (message) {
                const userType = this.messageUserType(message)

                if (parseInt(userType.indexOf('User')) > -1) {
                    return this.$locale.patient
                }
                else if (parseInt(userType.indexOf('Doctor')) > -1) {
                    return '医師'
                }
                return ''
            },
        },

        filters: {
            /**
             * ユーザタイプに応じてCSSクラス名を生成
             *
             * @param user_type
             * @param prefix
             * @returns {string}
             */
            morphUserType: function (user_type, prefix = '') {
                if (user_type === null) return ''

                if (parseInt(user_type.indexOf('User')) > -1) {
                    return prefix + 'users'
                }
                else if (parseInt(user_type.indexOf('Doctor')) > -1) {
                    return prefix + 'doctors'
                }
                else {
                    return ''
                }
            },
        },
    }
</script>

<style scoped lang="scss">
.consultation-message-pdf {
    width: 70px;
}

.p-thread {
    width: 100%;
    max-height: 400px;
    padding: 0 15px;
    list-style-type: none;
    overflow-y: scroll;

    &.p-thread--users {
        .p-chat--users {
            .p-chat__speaker {
                flex-direction: row-reverse;
            }

            .p-chat__speaker__info {
                text-align: right;
                margin-left: 0;
                margin-right: 10px;
            }

            .p-chat__body {
                text-align: left;
                max-width: 280px;
                margin-left: auto;

                .p-chat__body__content {
                    span, a {
                        white-space: pre-wrap;
                    }

                    a {
                        text-decoration: underline;

                        &:hover {
                            text-decoration: none;
                        }
                    }
                }
            }
        }

        .p-chat--doctors {
            .p-chat__body {
                margin-right: 0;
            }
        }
    }

    &.p-thread--doctors {
        .p-chat--doctors {
            .p-chat__speaker {
                flex-direction: row-reverse;
            }

            .p-chat__speaker__info {
                text-align: right;
                margin-left: 0;
                margin-right: 10px;
            }

            .p-chat__body {
                text-align: left;
                max-width: 280px;
                margin-left: auto;

                .p-chat__body__content {
                    span, a {
                        white-space: pre-wrap;
                    }

                    a {
                        text-decoration: underline;

                        &:hover {
                            text-decoration: none;
                        }
                    }
                }
            }
        }

        .p-chat--users {
            .p-chat__body {
                margin-right: 0;
            }
        }
    }
}

/*スクロールバー全体*/
.p-thread::-webkit-scrollbar {
    width: 5px;
}

/*スクロールバーの軌道*/
.p-thread::-webkit-scrollbar-track {
    border-radius: 10px;
    box-shadow: inset 0 0 6px rgba(0, 0, 0, .1);
}

/*スクロールバーの動く部分*/
.p-thread::-webkit-scrollbar-thumb {
    background-color: rgba(0, 0, 50, .5);
    border-radius: 10px;
    box-shadow: 0 0 0 1px rgba(255, 255, 255, .3);
}

/* .p-chat
 *________________________________________*/
.p-chat {
    margin-bottom: 25px;
    padding-bottom: 25px;
    border-bottom: 1px solid rgba(0, 0, 0, .07);
}

.p-chat__speaker__image,
.p-chat__speaker__image img {
    width: 45px;
    height: 45px;
    background: #ddd;
    border-radius: 50%;
    object-fit: cover;
    object-position: center;
}

.p-chat__speaker__info {
    margin-left: 10px;
}

.p-chat__speaker__info .text-muted span {
    font-size: 11px;
}

.p-chat__body {
    margin-top: 10px;
    max-width: 280px;

    .p-chat__body__content {
        span, a {
            white-space: pre-wrap;
        }

        a {
            text-decoration: underline;

            &:hover {
                text-decoration: none;
            }
        }
    }
}

.deletedImageBox {
    padding: 4px 0 4px 10px;
    background: #f8f8f8;
    border: 1px solid #e3e3e3;
    border-radius: 6px;
}
</style>
